// Adoption / health dashboard API client — thin typed wrapper over the shared
// authed axios instance (`api`). Talks to the super-admin-only backend surface
// at /api/v1/adoption/*. Auth header, base URL, and token refresh are handled
// by `api`; the path is allow-listed in apiWhitelist.ts.

import { api } from './api';

export interface DepartmentUsage {
  department: string;
  total_users: number;
  active_users: number;
  active_pct: number | null;
  runs: number;
  runs_per_active_user: number | null;
}

export interface OfficeLocationUsage {
  office_location: string;
  total_users: number;
  active_users: number;
  active_pct: number | null;
  runs: number;
  runs_per_active_user: number | null;
}

export interface UsageMetrics {
  window_days: number;
  total_users: number;
  active_users: number;
  active_pct: number | null;
  total_runs: number;
  runs_per_active_user: number | null;
  repeat_users: number;
  repeat_usage_rate: number | null;
  thumbs_up: number;
  thumbs_down: number;
  satisfaction_rate: number | null;
  by_department: DepartmentUsage[];
  by_office_location: OfficeLocationUsage[];
}

export interface ScoreAggregate {
  name: string;
  data_type: string;
  count: number;
  mean: number | null;
}

export interface DepartmentFeedback {
  department: string;
  thumbs_up: number;
  thumbs_down: number;
  satisfaction_rate: number | null;
}

export interface MetricValidationEntry {
  name: string;
  mean: number | null;
  passed: number | null;
  failed: number | null;
  total: number | null;
  pass_rate: number | null;
}

export interface MetricValidation {
  available: boolean;
  dataset_name: string;
  run_name: string | null;
  run_id: string | null;
  created_at: string | null;
  item_count: number | null;
  metrics: MetricValidationEntry[];
}

export interface QualityMetrics {
  window_days: number;
  thumbs_up: number;
  thumbs_down: number;
  satisfaction_rate: number | null;
  feedback_by_department: DepartmentFeedback[];
  scores: ScoreAggregate[];
  // Optional: absent when talking to a backend built before metric-validation
  // support. Callers must treat `undefined` as "not available".
  metric_validation?: MetricValidation;
  langfuse_available: boolean;
}

export interface NodeControl {
  node_name: string;
  complete: number;
  skipped: number;
  error: number;
  pass_rate: number | null;
}

export interface ModelCost {
  model: string;
  provider: string;
  total_tokens: number;
  total_cost_usd: number | null;
  call_count: number;
}

export interface OperationsMetrics {
  window_days: number;
  total_runs: number;
  completed_runs: number;
  failed_runs: number;
  success_rate: number | null;
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  latency_avg_ms: number | null;
  total_cost_usd: number | null;
  total_tokens: number;
  total_calls: number;
  controls_pass_rate: number | null;
  by_model: ModelCost[];
  node_controls: NodeControl[];
}

export interface AdoptionOverview {
  window_days: number;
  total_users: number;
  active_users: number;
  active_pct: number | null;
  total_runs: number;
  repeat_usage_rate: number | null;
  satisfaction_rate: number | null;
  success_rate: number | null;
  latency_p95_ms: number | null;
  total_cost_usd: number | null;
  controls_pass_rate: number | null;
  langfuse_available: boolean;
}

export interface UserSession {
  session_id: string;
  user_id: string | null;
  name: string | null;
  started_at: string | null;
  last_activity_at: string | null;
  duration_seconds: number | null;
  trace_count: number;
  total_cost_usd: number | null;
  langfuse_url: string | null;
}

export interface SessionsPage {
  since: string;
  until: string;
  session_count: number;
  total_traces: number;
  total_cost_usd: number | null;
  avg_traces_per_session: number | null;
  avg_duration_seconds: number | null;
  page: number;
  limit: number;
  has_more: boolean;
  approximate: boolean;
  sessions: UserSession[];
  langfuse_available: boolean;
}

// The two cross-filter facets, sent to every filtered endpoint.
export interface AdoptionFilter {
  departments?: string[];
  userIds?: string[];
}

export interface LeaderboardRow {
  user_id: string;
  user_label: string | null;
  run_count: number;
}

export interface RunLeaderboard {
  rows: LeaderboardRow[];
  total_users: number;
  page: number;
  limit: number;
  has_more: boolean;
}

// Minimal user shape for the picker (from GET /api/v1/admin/users).
export interface AdoptionUser {
  user_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  department: string;
  roles: string[];
}

// Serialize params so array facets become repeated keys (department=a&department=b),
// which is what FastAPI's `list[str]` query params expect (axios' default
// would emit role[]=a). Empty facets are omitted (→ platform-wide).
const serializeParams = (params: Record<string, unknown>): string => {
  const sp = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val == null) continue;
    if (Array.isArray(val)) val.forEach((v) => sp.append(key, String(v)));
    else sp.append(key, String(val));
  }
  return sp.toString();
};

const p = (days: number, f?: AdoptionFilter, extra?: Record<string, unknown>) => ({
  params: {
    days,
    department: f?.departments?.length ? f.departments : undefined,
    user_id: f?.userIds?.length ? f.userIds : undefined,
    ...extra,
  },
  paramsSerializer: serializeParams,
});

export const adoptionApi = {
  overview: async (days: number, f?: AdoptionFilter): Promise<AdoptionOverview> => {
    const res = await api.get<AdoptionOverview>('/api/v1/adoption/overview', p(days, f));
    return res.data;
  },
  usage: async (days: number): Promise<UsageMetrics> => {
    const res = await api.get<UsageMetrics>('/api/v1/adoption/usage', { params: { days } });
    return res.data;
  },
  quality: async (days: number, f?: AdoptionFilter): Promise<QualityMetrics> => {
    const res = await api.get<QualityMetrics>('/api/v1/adoption/quality', p(days, f));
    return res.data;
  },
  operations: async (days: number, f?: AdoptionFilter): Promise<OperationsMetrics> => {
    const res = await api.get<OperationsMetrics>('/api/v1/adoption/operations', p(days, f));
    return res.data;
  },
  // Recent Langfuse sessions over the window, paginated + filtered.
  sessions: async (days: number, f?: AdoptionFilter, page = 1, limit = 30): Promise<SessionsPage> => {
    const res = await api.get<SessionsPage>('/api/v1/adoption/sessions', p(days, f, { page, limit }));
    return res.data;
  },
  // Users ranked by total runs, respecting the department/user filter. Default top 5.
  runLeaderboard: async (
    days: number,
    f?: AdoptionFilter,
    page = 1,
    limit = 5,
  ): Promise<RunLeaderboard> => {
    const res = await api.get<RunLeaderboard>('/api/v1/adoption/run-leaderboard', p(days, f, { page, limit }));
    return res.data;
  },
  // Users for the picker.
  users: async (): Promise<AdoptionUser[]> => {
    const res = await api.get<AdoptionUser[]>('/api/v1/adoption/users');
    return res.data ?? [];
  },
};
